import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { navigate, withPrefix } from "gatsby";
import IconLookingGlass from "../IconLookingGlass";
import normalizeAndMatch from "../../utilities/normalizeAndMatch";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import cleanCompanyName from "../../utilities/cleanCompanyName";
import SupplyChainEntityTypeIcon from "../baitToPlate/BaitToPlateEntityTypeIcon";
import SupplyChainIndexItemFlags from "../baitToPlate/BaitToPlateIndexItemFlags";

const AutosuggestSearch = ({ searchIndex, getEntityHash, getEntityTitle, placeholderText = "Search..." }) => {
  const handleIndexSearch = (term, haystack) => {
    if ( term === "" ) {
       return haystack;
    }
    return haystack.filter(item => {
      if ( item.type === "vessel" ) {
        return (
          normalizeAndMatch(term, item.name)
          ||
          normalizeAndMatch(term, item.name_mandarin)
          ||
          normalizeAndMatch(term, item.imo)
          ||
          normalizeAndMatch(term, item.call_sign)
          ||
          normalizeAndMatch(term, item.mmsis[0])
        )
      }
      else {
        return (
          normalizeAndMatch(term, item.name)
        )
      }
    });
  }


  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    searchTerm === "" ? setSearchResults([]) :
    setSearchResults( handleIndexSearch(searchTerm, searchIndex) );
  },[searchTerm]);

  const [isFocused, setIsFocused] = useState(false);
  const [isSuggestListVisible, setIsSuggestListVisible] = useState(false);

  useEffect(() => {
    if ( isFocused && searchTerm !== "" ) {
      setIsSuggestListVisible(true);
    }
    else {
      //setIsSuggestListVisible(false);
    }
  }, [isFocused, searchTerm]);

  const supplyChainSearchRef = useRef(null);

  useOnClickOutside(supplyChainSearchRef, () => {
    setIsSuggestListVisible(false);
  });

  function handleSelectFromSuggestList() {
    setIsSuggestListVisible(false);
    setSearchTerm("");
  }

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);

  useEffect(() => {
    if ( isSuggestListVisible && searchResults.length > 0 ) {
      suggestionsRef?.current?.children[selectedItemIndex].scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [selectedItemIndex]);

  function getNextIndexValue() {
    if ( selectedItemIndex === null ) {
      return 0;
    }
    else if ( selectedItemIndex === searchResults.length - 1 ) {
      return searchResults.length - 1;
    }
    else {
      return selectedItemIndex + 1;
    }
  }

  function getPreviousIndexValue() {
    if ( selectedItemIndex === null ) {
      return searchResults.length - 1;
    }
    else if ( selectedItemIndex === 0 ) {
      return 0;
    }
    else {
      return selectedItemIndex - 1;
    }
  }

  function handleReturnKey() {
    if ( selectedItemIndex !== null ) {
      const item = searchResults[selectedItemIndex];
      if ( item ) {
        handleSelectFromSuggestList();
        const slug = getEntityHash(item);
        if ( slug.match(/^#/) ) {
          window.location.hash = slug;
        }
        else {
          // TK FIX
          navigate(slug.replace(/\/investigations\/china-the-superpower-of-seafood/, ""));
        }
        //window.location.hash = getEntityHash(item);
        inputRef.current.blur();
      }
    }
  }

  function handleKeyPress(event) {
    switch (event.keyCode) {
      case 13: // Enter key
        handleReturnKey();
        break;
      case 38: // Up Arrow key
        event.preventDefault()
        setSelectedItemIndex(getPreviousIndexValue());
        break;
      case 40: // Down Arrow key
        event.preventDefault()
        setSelectedItemIndex(getNextIndexValue());
        break;
      default:
        // Handle other keys if needed
        break;
    }
  }

  function handleInput(e) {
    setSearchTerm(e.target.value);
  }

  useEffect(() => {
    if ( searchResults.length > 0 ) {
      setSelectedItemIndex(0);
    }
    else {
      setSelectedItemIndex(null);
    }
  }, [searchResults]);

  return (
    <StyledSupplyChainSearch ref={supplyChainSearchRef}>
      <label htmlFor="supply-chain-search-input">
        <IconLookingGlass />
      </label>
      <input 
        ref={inputRef}
        id="supply-chain-search-input" 
        type="text" 
        placeholder={placeholderText} 
        autoComplete="off" 
        autoCapitalize="off" 
        spellCheck="false" 
        value={searchTerm} 
        onInput={(e) => handleInput(e)} 
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyPress}
      />
      <ol className={isSuggestListVisible ? "is-visible" : undefined} ref={suggestionsRef}>
        {searchTerm !== "" && (
         <>
          {searchResults.length === 0 ? (
            <li>No results found.</li>
          ) : (
            <>
              {searchResults.map((item, index) => (
                <li key={index} onClick={handleSelectFromSuggestList} className={selectedItemIndex === index ? "is-active" : undefined}>
                  <StyledSupplyChainSearchItem href={getEntityHash(item)} title={getEntityTitle(item)}>
                    <SupplyChainEntityTypeIcon type={item.type} />
                    <div>
                      <strong>{cleanCompanyName(item.name)}</strong>
                      <SupplyChainIndexItemFlags item={item} asSummary={true} />
                    </div>
                  </StyledSupplyChainSearchItem>
                </li>
              )
              )}
            </>
          )}
         </> 
        )}
      </ol>
    </StyledSupplyChainSearch>
  )
};

export default AutosuggestSearch;

const StyledSupplyChainSearch = styled.div`
  position: relative;
  display: grid;
  grid-template: 1fr / 2.5em 1fr;
  color: var(--color-black);
  max-width: 28em;
  .is-entity-view & {
    max-width: 100%;
  }
  > label {
    grid-row: 1;
    grid-column: 1;
    justify-self: center;
    align-self: center;
    display: grid;
    place-items: center;
  }
  input {
    grid-row: 1;
    grid-column: 1 / span 2;
    padding: 0.75em 1em 0.75em 2.75em;
    border-radius: 0.375em;
    border: 0;
    appearance: none;
    font-weight: 500;
    transition: box-shadow 0.2s ease;
    background-color: var(--color-white);
    &::placeholder {
      color: currentColor;
      transition: opacity 0.2s ease;
    }
    &:focus::placeholder {
      opacity: 0.5;
    }
    &:focus {
      outline: none;
      box-shadow: 0 0 1em var(--color-hr), 0 0 0 0.125em var(--color-hr);
      background-color: #fff;
    }

    
  }
  .is-entity-view & {
    grid-template: 1fr / 2em 1fr;
    > label {
      transform: scale(0.8);
    }
    input {
      padding: 0.5em 0.75em 0.5em 2em;
    }
  }
  ol {
    position: absolute;
    top: calc(100% - 0.28em);
    right: 0;
    left: 0;
    background-color: #fff;
    color: var(--color-black);
    list-style: none;
    padding: 0;
    margin: 0;
    z-index: 10;
    border-radius: 0 0 0.375em 0.375em;
    contain: paint;
    max-height: 18em;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    display: none;
    box-shadow: 0 0.125em 0.25em rgba(0,0,0,0.1);
    &.is-visible {
      display: block;
    }
    > li {
      padding: 0.25em 0.375em;
      &:not(:last-of-type) {
        border-bottom: 1px dotted rgba(0,0,0,0.1);
      }
    }
  }
`;

const StyledSupplyChainSearchItem = styled.a`
  position: relative;
  display: grid;
  grid-template: 1fr / 2.5em 1fr;
  gap: 0.5em;
  align-items: center;
  padding: 0.25em 1em 0.625em 0.25em;
  z-index: 0;
  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: -1;
    opacity: 0;
    border-radius: 0.375em;
    background-color: #f3f3f3;
    transition: opacity 0.2s ease;
  }
  &::after {
    box-shadow: 0 0 0 0.125em var(--color-hr);
  }
  .is-active > &::after {
    opacity: 1;
  }
  .can-hover &:hover,
  &:active {
    &::before {
      opacity: 1;
    }
  }
  strong {
    display: block;
    font-weight: 700;
    font-size: 0.9375em;
    margin-bottom: 0.25em;
  }
`;